import cx from 'classnames'
import { isEqual, isNil, noop, range } from 'lodash'
import React, { memo, useCallback, useContext, useMemo, useRef } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'

import * as Actions from 'actions'
import { insertLayer } from 'actions/insertLayer'
import PropTypes from 'prop-types'

import { assetsPaste } from 'actions/assetsPaste'
import { HOTKEYS, PLAYBACK_STATE, PLAYER_TYPE } from 'enums'
import { useHotkeys } from 'react-hotkeys-hook'
import { activeHotkeyProfileSelector } from 'selectors/user-profile-selector'
import { TimelineTopOffsetContext } from '~/components/Timeline/TimelineTopOffsetContext'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import MessageBox from '~/components/base/MessageBox'
import { useAction, useOnChange, useStatic } from '~/hooks'
import * as Selectors from '~/selectors'
import { TimelineLeftOffsetContextProvider } from '~/providers/TimelineLeftOffsetContextProvider'
import * as PT from '~/PropTypes'

import DnDContextProvider, { Context as DndContext } from './DnDContextProvider'
import DraggingItemProvider from './DraggingItemProvider'
import GeometryContextProvider from './GeometryContextProvider'
import { TimelineScrollPositionContext } from './ScrollPositionContext'
import './Timeline.scss'
import TimelineHeader from './TimelineHeader'
import TimelineWorkspace from './TimelineWorkspace'
import TimelineToolbar from './Toolbar'
import Countdown from './components/Countdown'
import { selectorActivePreview, selectPreviewMode, selectorPreviewClipCreator } from '~/selectors/preview'
import { setActivePreview, setInOutPointsTimelineAsset, setShowInOutPoints, resetMarkers } from '~/actions/preview'
import { PREVIEW_MODE } from '~/config/constants/preview'
import { clearAssetsSelection } from '~/actions/layer'
import { TIMELINE_DATASET_IDS } from '~/config/constants/timeline'
import { TransformUIDropContainer } from '~/components/TransformUIDropContainer'
import { Orientation } from '~/types/userInterface'
import { DROP_CONTAINER, UI_ELEMENT } from '~/config/constants/ui'
import { setActiveInteractionUIElement } from '~/actions/userInterface'
import { useTimelineAutoScroll } from '~/hooks/timeline/useTimelineAutoScroll'
import { SliderTimeContext } from '~/contexts/SliderTimeContext'
import { selectActiveInteractionUIElement } from '~/selectors/useInterface'
// ---

function EffectMonitorDraggingState() {
  const { isDraggingItemOverLayer } = React.useContext(DndContext)
  const dispatch = useDispatch()

  useOnChange(isDraggingItemOverLayer, isDragging => {
    if (isDragging) {
      // TODO:
      //  this will be executed over and over again, if dragged item is moved outside of timeline and back
      //  (i.e., during the same drag operation, without releasing mouse)
      //  Need to somehow check whether drag operation has been finished before re-emitting this action
      dispatch(Actions.timeline.startDraggingItemOverTimeline())
    } else {
      // When drag over timeline is finished (either mouse released, or just leaves timeline borders),
      // commit context time to store, so slider doesn't jump back where it was before drag started

      // dispatch(Actions.timeline.rewind(time))
    }
  })

  return null
}

function HotkeysListener({ disabled }) {
  const deleteSelectedAssets = useAction(Actions.timeline.deleteSelectedAssets)
  const deleteTimelineFragment = useAction(Actions.timeline.deleteTimelineFragment)
  const showSettings = useAction(Actions.timeline.showSettings)
  const nextEditingPoint = useAction(Actions.timeline.goClipEditingPoint)
  const prevEditingPoint = useAction(Actions.timeline.goClipEditingPoint, false)
  const copyAssets = useAction(Actions.layer.assetsCopy)
  const selectClipsAtCurrentPosition = useAction(Actions.layer.selectClipsAtCurrentPosition)
  const pasteAssets = useAction(assetsPaste)
  const copyTimelineFragment = useAction(Actions.layer.copyTimelineFragment)
  const activeInteractionUIElement = useSelector(selectActiveInteractionUIElement)
  const activeHotkeyProfile = useSelector(activeHotkeyProfileSelector)
  const selectedAssets = useSelector(Selectors.getSelectedAssets)
  const { timeline } = useSelector(selectorPreviewClipCreator)
  const ioPointsExists = !isNil(timeline.markIn) || !isNil(timeline.markOut)
  const isSelectedAssets = !!selectedAssets.length
  // eslint-disable-next-line no-nested-ternary
  const deleteAction = ioPointsExists && !isSelectedAssets
    ? deleteTimelineFragment
    : deleteSelectedAssets
  const copyAssetsAction = ioPointsExists && !isSelectedAssets
    ? copyTimelineFragment
    : copyAssets

  const isActiveTimeline = activeInteractionUIElement === UI_ELEMENT.TIMELINE
    && isSelectedAssets

  const prevPoint = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    prevEditingPoint()
  }, [ prevEditingPoint ])

  const nextPoint = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    nextEditingPoint()
  }, [ nextEditingPoint ])

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.DELETE],
    deleteAction, { enabled: !disabled && isActiveTimeline })
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.COPY],
    copyAssetsAction, { enabled: !disabled })
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.PASTE],
    pasteAssets, { enabled: !disabled })
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.SELECT_CLIPS_AT_CURRENT_POSITION],
    selectClipsAtCurrentPosition, { enabled: !disabled })
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.CLIP_SETTINGS],
    showSettings, { enabled: !disabled })
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.NEXT_EDITING_POINT],
    nextPoint, { enabled: !disabled })
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.PREV_EDITING_POINT],
    prevPoint, { enabled: !disabled })

  return null
}

function AutoplayTimer() {
  // It's important that timer *rewinds* timeline, not just moves slider.
  // So that players inside Preview will sync with this time.
  const { setLocalTime, localTimeRef } = useContext(SliderTimeContext)
  const pause = useAction(Actions.playback.changeTimelinePlaybackState, PLAYBACK_STATE.PAUSE)
  const latestEndTime = useSelector(Selectors.selectLatestEndTime)
  const timer = useRef()

  const refParams = useStatic(useSelector(state => ({
    sliderTime: state.timeline.sliderTime,
    isPlaying: state.playback.timelinePlaybackState === PLAYBACK_STATE.PLAY,
    activeAsset: Selectors.getPlayingMediaAssets(state)[0],
    mediaRecordingAssetInProgress: Selectors.recording.isRecordingStarted(state),
    isCapture: Selectors.recording.isRecordingCapture(state),
  })))
  const { isPlaying, isCapture, mediaRecordingAssetInProgress } = refParams.current

  React.useEffect(
    () => {
      let prevTime = performance.now()
      const playEmptyTimeline = () => {
        const localTime = localTimeRef.current
        const { activeAsset } = refParams.current
        const now = performance.now()
        const diffTime = now - prevTime
        if (diffTime < 16) {
          timer.current = requestAnimationFrame(playEmptyTimeline)
          return
        }
        const nextTime = localTime + diffTime * 1e4
        prevTime = now
        if (activeAsset === undefined) {
          if (nextTime >= latestEndTime && !mediaRecordingAssetInProgress) {
            setLocalTime(latestEndTime)
            pause()
          } else {
            setLocalTime(nextTime)
            timer.current = requestAnimationFrame(playEmptyTimeline)
          }
        }
      }

      if (isPlaying && !isCapture
        && (!refParams.current.activeAsset || mediaRecordingAssetInProgress)) {
        timer.current = requestAnimationFrame(playEmptyTimeline)
      }
      return () => {
        cancelAnimationFrame(timer.current)
      }
    },
    [ isPlaying, isCapture, refParams, setLocalTime, latestEndTime,
      pause, mediaRecordingAssetInProgress, refParams.current.activeAsset, localTimeRef ]
  )

  return null
}

const TimelineProvider = memo(({ scrollTop, children }) => {
  // onMouseDown сontrol for <div class = "timeline-header__ruller"/>
  const scrollLeft = useSelector(state => state.timeline.scrollLeft)
  const [ isSticky, setSliderSticky ] = React.useState(false)
  const refIsMouseDown = React.useRef(false)
  const timelineMouseDown = React.useRef(false)

  const scrollValue = useMemo(() => ({
    scrollLeft,
    scrollTop,
    refIsMouseDown,
    timelineMouseDown,
    setSliderSticky,
    isSticky,
  }), [
    scrollLeft,
    scrollTop,
    setSliderSticky,
    isSticky,
  ])

  return (
    <TimelineScrollPositionContext.Provider value={scrollValue}>
      {children}
    </TimelineScrollPositionContext.Provider>
  )
})

// ---

class Timeline extends React.Component {

  constructor(props) {
    super(props)
    this.timelineRef = React.createRef()
  }

  state = {
    scrollTop: 0,
  }

  shift = 0

  render() {
    const {
      layers,
      scale,
      isEmpty,
      onFocus,
      rewind,
      saveSettingsOpened,
      activeProjectLoading,
      showCountdown,
      setShowCountdown,
      mediaRecordingAssetInProgress,
      mediaRecordingCapture,
      modalError,
      onSetErrorMessage,
      scrollbarRef,
      layersContainerRef,
    } = this.props
    const { scrollTop } = this.state
    const timelineTopOffset = layersContainerRef.current?.getBoundingClientRect().top || 0

    return (
      <>
        <If condition={showCountdown}>
          <Countdown onCancel={() => setShowCountdown(false)} items={range(4).reverse()} />
        </If>
        <If condition={layers.length}>
          <div
            id="timeline"
            className={cx('timeline', { 'timeline--disabled': saveSettingsOpened || showCountdown, 'timeline--hidden': activeProjectLoading })}
            onFocus={onFocus}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex="0"
            onMouseDown={this.setTimelinePreview}
            ref={this.timelineRef}
          >
            <TransformUIDropContainer
              orientation={Orientation.Vertical}
              right={0}
              dropContainer={DROP_CONTAINER.TIMELINE_ON_RIGHT}
            />
            <HotkeysListener disabled={mediaRecordingAssetInProgress} />
            <AutoplayTimer />
            <TimelineToolbar disabled={mediaRecordingAssetInProgress} onMoveSlider={rewind} />
            <TimelineTopOffsetContext.Provider value={timelineTopOffset}>
              <TimelineProvider scrollTop={scrollTop}>
                <DnDContextProvider>
                  <DraggingItemProvider>
                    <TimelineLeftOffsetContextProvider>
                      <GeometryContextProvider>
                        {setTimelineRootRef => (
                          <div
                            className={cx('wrapper', { wrapper__disabled: true })}
                            style={{ overflow: 'hidden' }}
                            ref={setTimelineRootRef}
                          >
                            <TimelineHeader
                              scale={scale}
                              onMoveSlider={rewind}
                              disabled={saveSettingsOpened
                                || (mediaRecordingAssetInProgress && !mediaRecordingCapture)}
                              scrollbarRef={scrollbarRef}
                            />
                            <If condition={modalError}>
                              <MessageBox
                                className="timeline-error-modal"
                                message={modalError}
                                onClose={() => onSetErrorMessage(null)}
                              />
                            </If>
                            <TimelineWorkspace
                              ref={layersContainerRef}
                              scale={scale}
                              scrollbarRef={scrollbarRef}
                              layers={layers}
                              isEmpty={isEmpty}
                              onScroll={this.onScroll}
                              onDeniedDrop={onSetErrorMessage}
                            />

                            <EffectMonitorDraggingState />
                          </div>
                        )}
                      </GeometryContextProvider>
                    </TimelineLeftOffsetContextProvider>
                  </DraggingItemProvider>
                </DnDContextProvider>
              </TimelineProvider>
            </TimelineTopOffsetContext.Provider>
          </div>
        </If>

        <If condition={activeProjectLoading}>
          <CircularProgress
            size={100}
            endless
            percents={80}
            text="Loading.."
            transparent
          />
        </If>
      </>
    )
  }

  componentDidMount() {
    const { setScroll } = this.props
    setScroll()
    if (this.timelineRef.current) {
      this.timelineRef.current.addEventListener('mouseup', this.onSetInteractionUIElement)
    }
  }

  componentDidUpdate(prevProps) {
    const {
      layers,
      assetsByLayers,
      activeProjectLoading,
      activeProjectId,
    } = this.props

    if (this.timelineRef.current && layers.length && !prevProps.layers.length) {
      this.timelineRef.current.removeEventListener('mouseup', this.onSetInteractionUIElement)
      this.timelineRef.current.addEventListener('mouseup', this.onSetInteractionUIElement)
    }

    if (prevProps && prevProps.activeProjectId !== activeProjectId) {
      return
    }

    if ((!isEqual(prevProps.assetsByLayers, assetsByLayers) && !activeProjectLoading)
      || (prevProps.activeProjectLoading === true && activeProjectLoading === false)) {
      const { layers, insertLayer, draggingLayerId } = this.props

      if (prevProps.layers.at(-1)?.id !== layers.at(-1)?.id) {
        this.handleScrollTop()
      }
      if (layers.length && !draggingLayerId) {
        const lastLayer = layers[layers.length - 1]
        const assets = assetsByLayers.get(lastLayer.id)
        if (assets.length !== 0) {
          insertLayer(layers.length, true)
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.timelineRef.current) {
      this.timelineRef.current.removeEventListener('mouseup', this.onSetInteractionUIElement)
    }
  }

  onScroll = e => {
    const { setScrollLeft, storeScrollLeft } = this.props
    const { scrollLeft, scrollTop } = e.target

    if (storeScrollLeft !== scrollLeft) setScrollLeft(scrollLeft)
    this.setState(prev => prev.scrollTop !== scrollTop ? { ...prev, scrollTop } : prev)
  }

  onSetInteractionUIElement = () => {
    const { onSetActiveInteractionUIElement } = this.props
    onSetActiveInteractionUIElement(UI_ELEMENT.TIMELINE)
  }

  handleScrollLeft = shift => {
    const { scrollbarRef } = this.props
    if (scrollbarRef.current) {
      const scrollLeft = scrollbarRef.current.getScrollLeft()
      scrollbarRef.current.scrollLeft(shift + scrollLeft)
    }
  }

  handleScrollTop = (shift = null) => {
    const { scrollbarRef } = this.props
    if (scrollbarRef.current) {
      if (shift === null) {
        scrollbarRef.current.scrollToBottom()
        return
      }
      const scrollLeft = scrollbarRef.current.getScrollLeft()
      scrollbarRef.current.scrollLeft(shift + scrollLeft)
    }
  }

  setTimelinePreview = e => {
    const selectors = [ HOTKEYS.UNDO, HOTKEYS.REDO, HOTKEYS.ADD_TO_TIMELINE ]
      .map(id => `#${id}`)
      .join(', ')

    if (e.target.closest(selectors)) {
      return
    }
    const {
      activePreview,
      activePreviewMode,
      setTimelinePreview,
      resetIOPointsTimelineAsset,
      offIOPointsSourceMedia,
      resetMarkers,
      isSplitted,
      clearAssetsSelection,
    } = this.props

    if (activePreview !== PLAYER_TYPE.TIMELINE) {
      if (!isSplitted && (activePreviewMode === PREVIEW_MODE.AUTO)) {
        setTimelinePreview()
        resetIOPointsTimelineAsset()
        offIOPointsSourceMedia()
        resetMarkers()
      }
      if (isSplitted) {
        setTimelinePreview()
      }
    }
    if (Object.values(TIMELINE_DATASET_IDS).some(id => e.target.dataset?.id === id)) {
      clearAssetsSelection()
    }
  }

}

Timeline.defaultProps = {
  scale: 10.0,
  onFocus: () => {},
  saveSettingsOpened: false,
  storeScrollLeft: 0,
  activeProjectId: null,
  draggingLayerId: undefined,
  activePreview: PropTypes.string,
  activePreviewMode: PropTypes.string,
  setTimelinePreview: () => {},
  resetIOPointsTimelineAsset: () => {},
  offIOPointsSourceMedia: () => {},
  resetMarkers: () => {},

  clearAssetsSelection: noop,
  onSetActiveInteractionUIElement: noop,
  onSetErrorMessage: noop,
  isSplitted: false,
  modalError: null,
  scrollbarRef: null,
  layersContainerRef: null,
}

Timeline.propTypes = {
  layers: PropTypes.arrayOf(PT.Layer).isRequired,
  scale: PropTypes.number,
  storeScrollLeft: PropTypes.number,
  isEmpty: PropTypes.bool.isRequired,
  onFocus: PropTypes.func,
  rewind: PropTypes.func.isRequired,
  insertLayer: PropTypes.func.isRequired,
  setScrollLeft: PropTypes.func.isRequired,
  activeProjectLoading: PropTypes.bool.isRequired,
  assetsByLayers: PropTypes.instanceOf(Map).isRequired,
  saveSettingsOpened: PropTypes.bool,
  activeProjectId: PropTypes.string,
  draggingLayerId: PropTypes.string,
  mediaRecordingAssetInProgress: PropTypes.bool.isRequired,
  mediaRecordingCapture: PropTypes.bool.isRequired,
  showCountdown: PropTypes.bool.isRequired,
  setShowCountdown: PropTypes.func.isRequired,
  activePreview: PropTypes.string,
  activePreviewMode: PropTypes.string,
  setTimelinePreview: PropTypes.func,
  resetIOPointsTimelineAsset: PropTypes.func,
  offIOPointsSourceMedia: PropTypes.func,
  resetMarkers: PropTypes.func,
  clearAssetsSelection: PropTypes.func,
  onSetActiveInteractionUIElement: PropTypes.func,
  isSplitted: PropTypes.bool,
  modalError: PropTypes.string,
  onSetErrorMessage: PropTypes.func,
  setScroll: PropTypes.func.isRequired,
  scrollbarRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  layersContainerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
}

const mapStateToProps = state => ({
  modalError: state.mainView.errorMessage,
  visibleTimelineDuration: state.timeline.duration,
  playing: state.playback.timelinePlaybackState === PLAYBACK_STATE.PLAY,
  scale: state.timeline.scale,
  layers: state.timeline.layers,
  activeProjectLoading: state.projectData.activeProjectLoading || state.mainView.offlineRestoration,
  activeProjectLayersLength: state.projectData.activeProject?.layers.length || 0,
  activeProjectId: state.projectData.activeProjectId,
  isEmpty: Selectors.getAssets(state).length === 0,
  saveSettingsOpened: state.mainView.showSaveSettings,
  storeScrollLeft: state.timeline.scrollLeft,
  draggingLayerId: Selectors.selectDraggingLayerId(state),
  projectDuration: Selectors.getProjectDuration(state),
  mediaRecordingAssetInProgress: Selectors.recording.isRecordingStarted(state)
    || Selectors.recording.isRecordingSaving(state),
  mediaRecordingCapture: Selectors.recording.isRecordingCapture(state),
  showCountdown: Selectors.recording.selectShowCountdown(state),
  activePreview: selectorActivePreview(state),
  activePreviewMode: selectPreviewMode(state),
  isSplitted: state.preview.splitPlayers,
})

const mapDispatchToProps = dispatch => ({
  rewind: time => dispatch(Actions.timeline.rewind(time)),
  onSetErrorMessage: message => dispatch(Actions.mainView.setErrorMessage(message)),
  insertLayer: (index, isAdditional = false) => dispatch(
    insertLayer({ index, isHovering: false, isAdditional })
  ),
  setScrollLeft: scrollLeft => dispatch(Actions.timeline.setScrollLeft(scrollLeft)),
  setShowCountdown: value => dispatch(Actions.recording.setShowCountdown(value)),
  setTimelinePreview: () => dispatch(setActivePreview(PLAYER_TYPE.TIMELINE)),
  resetIOPointsTimelineAsset: () => dispatch(setInOutPointsTimelineAsset(null)),
  offIOPointsSourceMedia: () => dispatch(setShowInOutPoints(false, PLAYER_TYPE.MEDIA)),
  resetMarkers: () => dispatch(resetMarkers({ preview: PLAYER_TYPE.MEDIA })),
  clearAssetsSelection: () => dispatch(clearAssetsSelection()),
  onSetActiveInteractionUIElement: element => dispatch(setActiveInteractionUIElement(element)),
})

const TimelineWrapper = memo(props => {
  const scrollbarRef = React.useRef(null)
  const layersContainerRef = React.useRef(null)
  const assetsByLayers = useSelector(Selectors.getAssetsByLayers)

  const handleScrollLeft = React.useCallback(shift => {
    if (scrollbarRef.current) {
      const scrollLeft = scrollbarRef.current.getScrollLeft()
      scrollbarRef.current.scrollLeft(shift + scrollLeft)
    }
  }, [])

  const { setScroll } = useTimelineAutoScroll({
    scrollbarRef,
    handleScrollLeft,
  })

  return (
    <Timeline
      {...props}
      assetsByLayers={assetsByLayers}
      setScroll={setScroll}
      scrollbarRef={scrollbarRef}
      layersContainerRef={layersContainerRef}
      handleScrollLeft={handleScrollLeft}
    />
  )
})

export default connect(mapStateToProps, mapDispatchToProps)(TimelineWrapper)
